import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, Loader2, FolderOpen } from 'lucide-react';
import { ProjectAccess, UserRole } from '@/types/user';
import { projectAPI } from '@/utils/supabase/client';

interface ProjectAccessManagerProps {
  userId: string;
  userRole: UserRole;
}

interface Project {
  id: string;
  name?: string;
  projectName?: string; // API returns projectName in legacy format
  status: string;
}

// Helper to get the project name (handles both name and projectName fields)
const getProjectName = (project: Project): string => {
  return project.name || project.projectName || 'Unnamed Project';
};

export function ProjectAccessManager({ userId, userRole }: ProjectAccessManagerProps) {
  const [projectAccess, setProjectAccess] = useState<(ProjectAccess & { project?: Project })[]>([]);
  const [allProjects, setAllProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [selectedProject, setSelectedProject] = useState<string>('');

  useEffect(() => {
    loadData();
  }, [userId]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [accessData, projectsData] = await Promise.all([
        projectAPI.getUserAccess(userId),
        projectAPI.getProjects(),
      ]);
      setProjectAccess(accessData);
      setAllProjects(projectsData.filter((p: Project) => p.status !== 'canceled'));
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load project access. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAccess = async () => {
    if (!selectedProject) return;

    setSaving('add');
    try {
      await projectAPI.grantProjectAccess(userId, {
        projectId: selectedProject,
        permission: 'view',
        canViewFinancials: false,
        canViewPayments: false,
        canViewTeam: false,
      });
      await loadData();
      setSelectedProject('');
    } catch (error) {
      console.error('Failed to grant access:', error);
    } finally {
      setSaving(null);
    }
  };

  const handleUpdateAccess = async (
    projectId: string,
    updates: Partial<{
      permission: string;
      canViewFinancials: boolean;
      canViewPayments: boolean;
      canViewTeam: boolean;
    }>
  ) => {
    setSaving(projectId);
    try {
      // Re-grant with updated values
      const existing = projectAccess.find(a => a.project_id === projectId);
      await projectAPI.grantProjectAccess(userId, {
        projectId,
        permission: updates.permission ?? existing?.permission ?? 'view',
        canViewFinancials: updates.canViewFinancials ?? existing?.can_view_financials ?? false,
        canViewPayments: updates.canViewPayments ?? existing?.can_view_payments ?? false,
        canViewTeam: updates.canViewTeam ?? existing?.can_view_team ?? false,
      });
      await loadData();
    } catch (error) {
      console.error('Failed to update access:', error);
    } finally {
      setSaving(null);
    }
  };

  const handleRevokeAccess = async (projectId: string) => {
    setSaving(projectId);
    try {
      await projectAPI.revokeProjectAccess(userId, projectId);
      await loadData();
    } catch (error) {
      console.error('Failed to revoke access:', error);
    } finally {
      setSaving(null);
    }
  };

  // Get projects that the user doesn't have access to yet
  const availableProjects = allProjects.filter(
    p => !projectAccess.some(a => a.project_id === p.id)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-sm text-destructive mb-3">{error}</p>
        <Button variant="outline" size="sm" onClick={loadData}>
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label>Project Access</Label>
        <Badge variant="outline">{projectAccess.length} projects</Badge>
      </div>

      {/* Add Project Access */}
      {availableProjects.length > 0 && (
        <div className="flex gap-2">
          <Select value={selectedProject} onValueChange={setSelectedProject}>
            <SelectTrigger className="flex-1">
              <SelectValue placeholder="Select a project to grant access..." />
            </SelectTrigger>
            <SelectContent>
              {availableProjects.map((project) => (
                <SelectItem key={project.id} value={project.id}>
                  {getProjectName(project)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="icon"
            onClick={handleAddAccess}
            disabled={!selectedProject || saving === 'add'}
          >
            {saving === 'add' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
          </Button>
        </div>
      )}

      {/* Project Access List */}
      {projectAccess.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <FolderOpen className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No project access granted yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {projectAccess.map((access) => {
            const isLoading = saving === access.project_id;
            return (
              <div
                key={access.id}
                className="p-3 rounded-lg border bg-muted/30 space-y-3"
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm">
                    {access.project ? getProjectName(access.project) : 'Unknown Project'}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRevokeAccess(access.project_id)}
                    disabled={isLoading}
                    className="h-8 w-8 text-destructive hover:text-destructive"
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                </div>

                {/* Permission Level */}
                <div className="flex items-center gap-2">
                  <Label className="text-xs w-20">Permission</Label>
                  <Select
                    value={access.permission}
                    onValueChange={(value) =>
                      handleUpdateAccess(access.project_id, { permission: value })
                    }
                    disabled={isLoading}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="view">View Only</SelectItem>
                      <SelectItem value="edit">Can Edit</SelectItem>
                      <SelectItem value="admin">Full Access</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Visibility Options (for external partners) */}
                {userRole === 'external_partner' && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">View Financials</Label>
                      <Switch
                        checked={access.can_view_financials}
                        onCheckedChange={(checked) =>
                          handleUpdateAccess(access.project_id, { canViewFinancials: checked })
                        }
                        disabled={isLoading}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">View Payments</Label>
                      <Switch
                        checked={access.can_view_payments}
                        onCheckedChange={(checked) =>
                          handleUpdateAccess(access.project_id, { canViewPayments: checked })
                        }
                        disabled={isLoading}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">View Team</Label>
                      <Switch
                        checked={access.can_view_team}
                        onCheckedChange={(checked) =>
                          handleUpdateAccess(access.project_id, { canViewTeam: checked })
                        }
                        disabled={isLoading}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
